import { useState, useCallback, useEffect } from 'react';
import { useRequestPasswordResetMutation } from '../api/authApi';
import { PASSWORD_RESET_COOLDOWN_MINUTES, PASSWORD_RESET_COOLDOWN_KEY_PREFIX } from '../constants';

export const useRestorePassword = () => {
  const [email, setEmail] = useState('');
  const [prevEmail, setPrevEmail] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [resetPasswordMutation, { isLoading, error }] = useRequestPasswordResetMutation();

  // Email validation regex
  const isEmailValid = /.+@.+\..+/.test(email);

  // Get cooldown key for this email
  const getCooldownKey = useCallback(
    (emailAddr: string) => PASSWORD_RESET_COOLDOWN_KEY_PREFIX + emailAddr.toLowerCase(),
    []
  );

  // Check if email has active cooldown
  const checkCooldown = useCallback(
    (emailAddr: string) => {
      const key = getCooldownKey(emailAddr);
      const storedTime = localStorage.getItem(key);

      if (!storedTime) {
        return 0; // No cooldown
      }

      const lastRequestTime = parseInt(storedTime, 10);
      const now = Date.now();
      const elapsedSeconds = Math.floor((now - lastRequestTime) / 1000);
      const cooldownSeconds = PASSWORD_RESET_COOLDOWN_MINUTES * 60;

      if (elapsedSeconds >= cooldownSeconds) {
        // Cooldown expired
        localStorage.removeItem(key);
        return 0;
      }

      // Cooldown still active
      return cooldownSeconds - elapsedSeconds;
    },
    [getCooldownKey]
  );

  // Set cooldown timestamp
  const setCooldown = useCallback(
    (emailAddr: string) => {
      const key = getCooldownKey(emailAddr);
      localStorage.setItem(key, Date.now().toString());
    },
    [getCooldownKey]
  );

  if (email !== prevEmail) {
    setPrevEmail(email);
    setRemainingSeconds(email ? checkCooldown(email) : 0);
  }

  const isCooldownRunning = remainingSeconds > 0;

  useEffect(() => {
    if (!isCooldownRunning) return;

    const interval = window.setInterval(() => {
      setRemainingSeconds((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => window.clearInterval(interval);
  }, [isCooldownRunning]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!isEmailValid || isLoading || remainingSeconds > 0) {
      return;
    }

    try {
      setSuccessMessage('');
      const result = await resetPasswordMutation({ email }).unwrap();

      if (result.Success) {
        // Set cooldown after successful submission
        setCooldown(email);
        setSuccessMessage(result.Message);
        setRemainingSeconds(PASSWORD_RESET_COOLDOWN_MINUTES * 60);
      }
    } catch (err) {
      // Error is handled by RTK Query and exposed via the error state
      console.error('Password reset error:', err);
    }
  }, [email, isEmailValid, isLoading, remainingSeconds, resetPasswordMutation, setCooldown]);

  // Format remaining time as "X min Y sec"
  const formatRemainingTime = () => {
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    return `${minutes}m ${seconds}s`;
  };

  return {
    email,
    setEmail,
    isEmailValid,
    isLoading,
    error,
    successMessage,
    remainingSeconds,
    formatRemainingTime,
    handleSubmit,
  };
};

export const clearPasswordResetCooldowns = () => {
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith(PASSWORD_RESET_COOLDOWN_KEY_PREFIX)) {
      localStorage.removeItem(key);
    }
  });
};
